import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link as RouterLink} from 'react-router';
import {
    Alert,
    AlertTitle,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    GlobalStyles,
    Grid,
    IconButton,
    Link,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowsRotate, faCode, faPlus} from '@fortawesome/pro-solid-svg-icons';
import {useSnackbar} from 'notistack';
import {EventCalendar} from '@mui/x-scheduler/event-calendar';
import {SchedulerEvent} from '@mui/x-scheduler/models';
import {deDE} from '@mui/x-scheduler/locales';
import {de as deDateLocale} from 'date-fns/locale';
import dayjs, {Dayjs} from 'dayjs';

import {useApplicationContext} from 'ApplicationContext';
import {useDocumentTitle} from 'Hooks/useDocumentTitle';
import {
    CalendarAccessDto,
    CalendarEmployeeDto,
    CalendarViewDto,
    EUserGroup,
    OutlookCalendarViewDto,
} from 'apiClient/Api';

import {
    CalendarEventDetail,
    buildSources,
    toOutlookEvents,
    toSchedulerEvents,
    wallClock,
} from './calendarSources';
import EventDetailDialog from './EventDetailDialog';
import EntryEditorDialog, {EntryDraft} from './EntryEditorDialog';
import MobileAgenda from './MobileAgenda';
import {apiErrorMessage} from './errorText';
import TimeEntryDialog from 'Pages/Tasks/TimeEntryDialog';

/** Die Ansichten des Kalenders. Entspricht `CalendarView` des Schedulers. */
type CalendarView = 'day' | 'week' | 'month' | 'agenda';

/**
 * Der eingebaute Termin-Dialog zeigt die Beschreibung als einfachen Text. Er ist nur noch der
 * Rückfall (siehe `findClickedEventId`), soll dann aber lesbar sein — mehrzeilige Inhalte liefen
 * sonst zu einer Wurst zusammen. Er hängt in einem Portal außerhalb dieser Seite, deshalb geht das
 * nur über eine globale Regel und nicht über `sx`.
 */
const dialogStyles = (
    <GlobalStyles styles={{
        '.MuiEventCalendar-eventDialogDescriptionLabel': {
            whiteSpace: 'pre-line',
        },
    }}/>
);

/**
 * Die Termin-Elemente des Kalenders in allen Ansichten (Zeitraster, Monatsraster, Agenda).
 * Nur der Titel-Knoten trägt einen erkennbaren Klassennamen, den wir zur Zuordnung brauchen.
 */
const EVENT_TITLE_SELECTOR = [
    '[class*="-timeGridEventTitle"]',
    '[class*="-dayGridEventTitle"]',
    '[class*="-eventItemTitle"]',
].join(',');

/**
 * Ermittelt, welcher Termin angeklickt wurde.
 *
 * **Warum über das DOM:** `@mui/x-scheduler` 9.0.0-beta.9 bietet keinen Klick-Hook an — die
 * öffentliche API kennt nur `setVisibleDate`, es gibt keine Slots und kein `onEventClick`. Ohne
 * diesen Umweg gäbe es keinen eigenen Dialog und damit keinen Knopf in die Teams-Besprechung.
 *
 * **Warum das vertretbar ist:** Findet sich kein Titel-Element oder passt der Titel zu keinem
 * bekannten Termin, gibt die Funktion `null` zurück — dann läuft der Klick unverändert weiter und
 * der eingebaute Dialog öffnet wie bisher. Ändert die Bibliothek ihre Klassennamen, verliert man
 * also Komfort, keine Funktion.
 */
const findClickedEventId = (
    target: HTMLElement,
    events: {id: string | number; title: string; start: string}[],
): string | null => {
    const card = target.closest('[class*="EventCalendar-"]');
    if (!card) return null;

    const titleNode = card.querySelector(EVENT_TITLE_SELECTOR)
        ?? target.closest(EVENT_TITLE_SELECTOR);
    const title = titleNode?.textContent?.trim();
    if (!title) return null;

    const matches = events.filter((e) => e.title.trim() === title);
    if (matches.length === 0) return null;

    // Mehrere Termine mit gleichem Titel im Blick: über die im Element stehende Uhrzeit
    // eingrenzen. Bleibt es mehrdeutig, gewinnt der erste — schlechter als exakt, aber besser
    // als kein Dialog.
    if (matches.length > 1) {
        const time = card.textContent?.match(/\b(\d{1,2}):(\d{2})\b/);
        if (time) {
            const wanted = `${time[1].padStart(2, '0')}:${time[2]}`;
            const exact = matches.find((e) => e.start.slice(11, 16) === wanted);
            if (exact) return String(exact.id);
        }
    }

    return String(matches[0].id);
};

/**
 * Die Labelwin-Satznummer hinter einer Termin-Id des Rasters.
 *
 * Die Ids der Plantafel-Einträge tragen das Präfix `e` (siehe `toSchedulerEvents`); Wiedervorlagen
 * (`t`), Outlook-Termine (`o`) und die vom Kalender selbst erzeugten Entwürfe gehören nicht dazu
 * und liefern deshalb `null`.
 */
const entryIdOf = (id: string | number): number | null => {
    const text = String(id);
    if (!text.startsWith('e')) return null;

    const parsed = Number(text.slice(1));
    return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Wandelt den Datumstyp des Kalenders in dayjs.
 *
 * Der Scheduler gibt seinen internen Typ heraus (Date, ISO-Zeichenkette oder ein
 * Temporal-Objekt) — der Typ ist bewusst offen. dayjs versteht die ersten beiden Formen direkt,
 * für die dritte hilft der Umweg über die Zeichenkette.
 */
const toDayjs = (value: unknown): Dayjs => {
    const direct = dayjs(value as never);
    return direct.isValid() ? direct : dayjs(String(value));
};

/**
 * Der eigene Kalender.
 *
 * Die Person ist nie Teil der Anfrage — der Server leitet sie aus dem LabelBenutzer des
 * angemeldeten Kontos ab. Diese Seite kann deshalb keinen fremden Kalender anzeigen, und es gibt
 * hier bewusst keine Personenauswahl.
 *
 * Vor dem ersten Abruf wird `GET /Calendar/Me/Access` gefragt. Der antwortet immer mit 200 und
 * sagt in Klartext, ob und warum der Kalender nicht zur Verfügung steht ("kein LabelBenutzer
 * hinterlegt" ist der häufigste Fall und ein Zustand, kein Fehler) — so muss die Seite nicht erst
 * in ein 403 laufen, um das herauszufinden.
 *
 * Dargestellt wird mit dem `EventCalendar` aus MUI X. Die Herkunft der Termine steckt in dessen
 * Ressourcen-Baum (siehe `calendarSources.ts`), damit Labelwin und Microsoft 365 in einem Raster
 * liegen und trotzdem einzeln ein- und ausblendbar sind.
 */
export const MyCalendar: React.FC = () => {
    const {t, i18n} = useTranslation();
    useDocumentTitle('calendar.title');
    const context = useApplicationContext();

    const [access, setAccess] = useState<CalendarAccessDto | undefined>(undefined);
    const [accessLoading, setAccessLoading] = useState(true);
    const [view, setView] = useState<CalendarViewDto | undefined>(undefined);
    const [outlook, setOutlook] = useState<OutlookCalendarViewDto | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const theme = useTheme();
    /** Telefon hochkant — dort ist ein Wochenraster mit sieben Spalten nicht zu gebrauchen. */
    const compact = useMediaQuery(theme.breakpoints.down('sm'));

    const [visibleDate, setVisibleDate] = useState<Dayjs>(() => dayjs().startOf('day'));
    /*
     * Auf dem Telefon beginnt der Kalender in der Terminliste: sieben Spalten auf einer Handbreite
     * ergeben Streifen von vierzig Pixeln, in denen kein Betreff mehr steht. Umschalten kann man
     * jederzeit — die Vorgabe soll nur mit dem anfangen, was dort auch lesbar ist.
     */
    const [calendarView, setCalendarView] = useState<CalendarView>(compact ? 'agenda' : 'week');
    const [showRaw, setShowRaw] = useState(false);

    // Fremder Kalender: `null` heißt „mein eigener". Nur Administratoren können hier etwas wählen.
    const [person, setPerson] = useState<CalendarEmployeeDto | null>(null);
    const [employees, setEmployees] = useState<CalendarEmployeeDto[]>([]);

    /** Der Termin, dessen Detail-Dialog offen ist. */
    const [selected, setSelected] = useState<CalendarEventDetail | null>(null);

    /** Auftragsnummer, für die der Zeitbuchungs-Dialog offen ist; `undefined` = zu. */
    const [timeOrder, setTimeOrder] = useState<string | undefined>(undefined);
    const canBookTime = context.hasPermission('labelwin.time.book');

    const {enqueueSnackbar} = useSnackbar();
    /** Entwurf für den Anlegen-Dialog; `undefined` = Dialog zu. */
    const [entryDraft, setEntryDraft] = useState<EntryDraft | null | undefined>(undefined);
    const [saving, setSaving] = useState(false);

    const isAdmin = (context.userData?.userGroup ?? EUserGroup.CUSTOMER) <= EUserGroup.ADMIN;
    const isGerman = i18n.language.toLowerCase().startsWith('de');

    /**
     * Die Kennung, unter der eine Person adressiert wird. Für Labelwin ist beides gleichwertig
     * (dort wird über Mail, interne Mail und LabelBenutzer gesucht); für Outlook ist die externe
     * Adresse die bessere Wahl, weil sie im Haus dem Anmeldenamen in Microsoft 365 entspricht.
     */
    const personKey = person?.mail || person?.mailInternal || person?.labelUser || undefined;

    /*
     * Geholt wird immer der ganze Monat auf volle Wochen erweitert — unabhängig von der Ansicht.
     * Damit deckt eine Anfrage Tag, Woche, Monat und Agenda ab: das Umschalten der Ansicht und
     * das Blättern innerhalb des Monats kommen ohne neuen Abruf aus, und im Monatsraster sind die
     * angeschnittenen Tage der Nachbarmonate mit dabei.
     */
    const range = useMemo(() => {
        const first = visibleDate.startOf('month');
        const last = visibleDate.endOf('month').startOf('day');
        return {
            from: first.subtract((first.day() + 6) % 7, 'day'),
            to: last.add(6 - ((last.day() + 6) % 7), 'day'),
        };
    }, [visibleDate]);

    useEffect(() => {
        setAccessLoading(true);
        context.api.calendar
            .calendarGetMyAccess()
            .then((response) => setAccess(response.data))
            .catch(context.apiError)
            .finally(() => setAccessLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /*
     * Beide Quellen werden **unabhängig** geholt. Fällt LabelConnect aus, sollen die
     * Outlook-Termine trotzdem erscheinen — und umgekehrt. Deshalb zwei Abrufe, zwei Zustände und
     * kein gemeinsames `catch`, das eine Quelle die andere mitnehmen lässt.
     */
    // Mitarbeiterliste für die Personenauswahl. Nur Administratoren dürfen den Endpunkt abrufen.
    useEffect(() => {
        if (!isAdmin) return;
        context.api.labelConnect
            .labelConnectGetEmployees()
            .then((response) => setEmployees(response.data))
            // Still: ohne Liste bleibt die Auswahl leer, der eigene Kalender funktioniert weiter.
            .catch(() => setEmployees([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    const load = useCallback(() => {
        if (!access?.available) return;

        const von = range.from.format('YYYY-MM-DD');
        const bis = range.to.format('YYYY-MM-DD');

        setLoading(true);
        setError(undefined);

        const labelwin = (personKey
            ? context.api.calendar.calendarGetCalendarForUser({user: personKey, von, bis})
            : context.api.calendar.calendarGetMyCalendar({von, bis}))
            .then((response) => setView(response.data))
            .catch((e: any) => {
                // Die Anbindung kann zwischen Access-Abfrage und Abruf wegbrechen (503) — dann
                // gehört die Begründung des Servers auf die Seite, nicht in eine Snackbar.
                setError(apiErrorMessage(e, t('calendar.loadFailed')));
                setView(undefined);
            });

        // Der Outlook-Endpunkt antwortet immer mit 200 und begründet sich selbst; ein `catch`
        // deckt nur den Transportfehler ab und bleibt bewusst still.
        const microsoft = (personKey
            ? context.api.calendar.calendarGetOutlookCalendarForUser({user: personKey, von, bis})
            : context.api.calendar.calendarGetMyOutlookCalendar({von, bis}))
            .then((response) => setOutlook(response.data))
            .catch(() => setOutlook(undefined));

        Promise.all([labelwin, microsoft]).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [access?.available, range.from.valueOf(), range.to.valueOf(), personKey]);

    useEffect(() => {
        load();
    }, [load]);

    const outlookAvailable = outlook?.available ?? false;

    const {events, details} = useMemo(() => {
        const labelwin = toSchedulerEvents(view, t);
        const microsoft = toOutlookEvents(outlook, t);

        // forEach statt Spread: das Übersetzungsziel des Projekts kann Map-Iteratoren nicht
        // abbilden (dieselbe Einschränkung wie an anderen Stellen im Client).
        const merged = new Map<string, CalendarEventDetail>();
        labelwin.details.forEach((value, key) => merged.set(key, value));
        microsoft.details.forEach((value, key) => merged.set(key, value));

        return {events: [...labelwin.events, ...microsoft.events], details: merged};
    }, [view, outlook, t]);

    const resources = useMemo(() => buildSources(t, outlookAvailable), [t, outlookAvailable]);

    /**
     * Klick auf einen Termin: eigenen Dialog öffnen und den eingebauten unterdrücken.
     *
     * Läuft in der Erfassungsphase, damit der Kalender den Klick nicht zuerst sieht. Lässt sich der
     * Termin nicht zuordnen, wird nichts unterdrückt — dann öffnet wie bisher der eingebaute
     * Dialog, der dieselben Angaben als Text enthält.
     */
    const handleCalendarClickCapture = (event: React.MouseEvent<HTMLDivElement>): void => {
        const id = findClickedEventId(
            event.target as HTMLElement,
            events.map((e) => ({id: e.id, title: e.title, start: e.start})));
        if (!id) return;

        const found = details.get(id);
        if (!found) return;

        event.preventDefault();
        event.stopPropagation();
        setSelected(found);
    };

    /*
     * Geschrieben wird nur im **eigenen** Kalender: die Person ist am Server nicht Teil der
     * Anfrage (ADR-019), ein Schreibversuch in einem fremden Kalender liefe zwangsläufig in ein
     * 403. Ist eine andere Person gewählt, bleibt das Raster deshalb schreibgeschützt.
     */
    const canWrite = (access?.allowWrites ?? false) && person === null;

    /**
     * Überträgt eine Änderung aus dem Raster an den Server.
     *
     * Der Kalender meldet keinen Unterschied, sondern die **ganze** neue Terminliste — was sich
     * geändert hat, wird deshalb hier gegen den bisherigen Stand ermittelt. Nacheinander statt
     * parallel: Projdat kennt keine Transaktionen, und bei einem Fehler soll erkennbar bleiben,
     * was schon geschrieben wurde.
     */
    const applyChanges = async (next: SchedulerEvent[]): Promise<void> => {
        const before = new Map(events.map((e) => [String(e.id), e]));
        const after = new Map(next.map((e) => [String(e.id), e]));

        // Ein vom Kalender erzeugter Entwurf trägt eine eigene Id ohne unser `e`-Präfix.
        const created = next.filter((e) => !before.has(String(e.id)));
        const removed = events.filter((e) => !after.has(String(e.id)));
        const moved = next.filter((e) => {
            const previous = before.get(String(e.id));
            return previous !== undefined
                && (wallClock(previous.start) !== wallClock(e.start)
                    || wallClock(previous.end) !== wallClock(e.end)
                    || previous.title !== e.title);
        });

        if (created.length === 0 && removed.length === 0 && moved.length === 0) return;

        setSaving(true);
        try {
            for (const event of created) {
                // Ohne Art angelegt = freier Termin. Urlaub und die anderen Abwesenheiten gehen
                // über den eigenen Dialog, weil das Raster dafür kein Feld hat.
                await context.api.calendar.calendarCreateEntry({
                    start: wallClock(event.start),
                    end: wallClock(event.end),
                    subject: event.title?.trim() || t('calendar.untitled'),
                });
            }

            for (const event of moved) {
                const entryId = entryIdOf(event.id);
                if (entryId === null) continue;

                await context.api.calendar.calendarMoveEntry(entryId, {
                    start: wallClock(event.start),
                    end: wallClock(event.end),
                    subject: event.title?.trim() || undefined,
                });
            }

            for (const event of removed) {
                const entryId = entryIdOf(event.id);
                if (entryId === null) continue;

                await context.api.calendar.calendarDeleteEntry(entryId);
            }

            enqueueSnackbar(t('calendar.saved'), {variant: 'success'});
        } catch (e) {
            setError(apiErrorMessage(e, t('calendar.saveFailed')));
        } finally {
            setSaving(false);
            /*
             * Immer neu laden — auch nach einem Fehler. Sonst stünde im Raster eine Änderung, die
             * es in der Plantafel nicht gibt. Und nach Erfolg zählt ohnehin nur, was der Server
             * liefert: Labelwin legt beim Ändern neu an, die Satz-Id wandert dabei.
             */
            load();
        }
    };

    /** Löscht einen eigenen Eintrag aus dem Detail-Dialog heraus. */
    const deleteEntry = async (entryId: number): Promise<void> => {
        setSaving(true);
        setSelected(null);
        try {
            await context.api.calendar.calendarDeleteEntry(entryId);
            enqueueSnackbar(t('calendar.deleted'), {variant: 'success'});
        } catch (e) {
            setError(apiErrorMessage(e, t('calendar.deleteFailed')));
        } finally {
            setSaving(false);
            load();
        }
    };

    // Die deutschen Texte des Kalenders liegen als Theme-Ergänzung vor; hier wird nur der
    // Textteil entnommen, damit die Umstellung der Sprache ohne Theme-Neubau greift.
    const localeText = isGerman
        ? deDE.components.MuiEventCalendar.defaultProps.localeText
        : undefined;

    if (accessLoading) {
        return (
            <Grid size={12}>
                <Box sx={{display: 'flex', justifyContent: 'center', p: 6}}><CircularProgress/></Box>
            </Grid>
        );
    }

    if (!access?.available) {
        return (
            <Grid size={12} sx={{maxWidth: 900}}>
                <Typography variant="h5" gutterBottom>{t('calendar.title')}</Typography>
                <Alert severity={access?.hasLabelUser === false ? 'info' : 'warning'} sx={{mt: 2}}>
                    <AlertTitle>{t('calendar.unavailableTitle')}</AlertTitle>
                    {access?.message}
                    {access?.hasLabelUser === false && isAdmin && (
                        <Box sx={{mt: 1.5}}>
                            <Link component={RouterLink} to="/admin/users">{t('calendar.goToUsers')}</Link>
                        </Box>
                    )}
                    {access?.hasLabelUser === false && !isAdmin && (
                        <Box sx={{mt: 1.5}}>
                            <Typography variant="body2">{t('calendar.askAdmin')}</Typography>
                        </Box>
                    )}
                </Alert>
            </Grid>
        );
    }

    return (
        <Grid size={12}>
            {dialogStyles}

            {/* Kopf: wessen Kalender, Zustand, Neuladen. Blättern und Ansicht macht der Kalender selbst. */}
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={{xs: 1, sm: 1.5}}
                   sx={{alignItems: {sm: 'center'}, justifyContent: 'space-between', mb: {xs: 1.25, sm: 2}}}>
                <Box>
                    <Typography variant={compact ? 'h6' : 'h5'}>
                        {person ? t('calendar.titleForeign', {name: person.name}) : t('calendar.title')}
                    </Typography>
                    {/*
                      * Personalnummer und LabelBenutzer sind Angaben zum Nachschlagen, nicht zum
                      * Arbeiten — auf dem Telefon kostet die Zeile mehr, als sie einbringt.
                      */}
                    {!compact && (
                        <Typography variant="body2" color="text.secondary">
                            {t('calendar.subtitle', {
                                name: (person ?? view?.employee ?? access.employee)?.name ?? '—',
                                employeeId: (person ?? view?.employee ?? access.employee)?.employeeId ?? '—',
                                labelUser: (person ?? view?.employee)?.labelUser ?? access.labelUser ?? '—',
                            })}
                        </Typography>
                    )}
                </Box>

                <Stack direction="row" spacing={1} sx={{
                    alignItems: 'center', flexWrap: 'wrap', rowGap: 1, width: {xs: '100%', sm: 'auto'},
                }}>
                    {/*
                      * Personenauswahl nur für Administratoren — der Server lässt den Fremdzugriff
                      * auch nur für sie zu und protokolliert jeden. Leeres Feld = eigener Kalender.
                      */}
                    {isAdmin && (
                        <Autocomplete
                            size="small"
                            // Feste 260 Pixel passen nicht in die rund 290, die auf einem Telefon
                            // übrig bleiben — das war die Ursache des Querscrollens.
                            sx={{minWidth: {xs: 0, sm: 260}, width: {xs: '100%', sm: 'auto'}}}
                            options={employees}
                            value={person}
                            onChange={(_, value) => setPerson(value)}
                            getOptionLabel={(option) => option.name ?? option.labelUser ?? '—'}
                            isOptionEqualToValue={(a, b) => a.employeeId === b.employeeId}
                            renderInput={(params) => (
                                <TextField {...params} label={t('calendar.personLabel')}
                                           placeholder={t('calendar.personPlaceholder')}/>
                            )}
                        />
                    )}
                    {canWrite && (
                        <Button size="small" variant="contained"
                                startIcon={<FontAwesomeIcon icon={faPlus}/>}
                                onClick={() => setEntryDraft(null)} disabled={saving}>
                            {t('calendar.newEntry')}
                        </Button>
                    )}
                    {(loading || saving) && <CircularProgress size={16}/>}
                    <Chip size="small" label={t('calendar.entryCount', {count: events.length})}/>
                    {!access.allowWrites && (
                        <Chip size="small" variant="outlined" label={t('calendar.readOnly')}/>
                    )}
                    {/*
                      * Fehlt Outlook, sagt der Server in Klartext warum. Als Chip mit Erklärung
                      * statt als Warnbalken: für Konten ohne Microsoft-Postfach ist das ein
                      * Dauerzustand, kein Vorfall.
                      */}
                    {outlook && !outlookAvailable && (
                        <Tooltip title={outlook.message ?? ''}>
                            <Chip size="small" variant="outlined" color="default"
                                  label={t('calendar.outlookUnavailable')}/>
                        </Tooltip>
                    )}
                    <Tooltip title={t('calendar.reload')}>
                        <IconButton size="small" onClick={load} disabled={loading}>
                            <FontAwesomeIcon icon={faArrowsRotate}/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            {error && <Alert severity="error" sx={{mb: 2}} onClose={() => setError(undefined)}>{error}</Alert>}

            {/*
              * Der Kalender braucht eine feste Höhe — er verteilt seinen Inhalt selbst darauf und
              * scrollt intern. Ohne Höhe fällt er auf null zusammen.
              */}
            {/*
              * Auf dem Telefon die eigene Terminliste statt des Rasters.
              *
              * `EventCalendar` ist dort nicht zu gebrauchen: die Datumsspalte nimmt die halbe
              * Breite, der Betreff daneben wird abgeschnitten, und jeder leere Tag verbraucht
              * trotzdem eine volle Zeile. An den Innereien des Bausteins ist über CSS nicht
              * verlässlich heranzukommen — die Klassennamen sind unstabil (siehe
              * `findClickedEventId`). Die eigene Liste kennt dieselben Termine und öffnet denselben
              * Detail-Dialog; nur die Darstellung ist eine, die auf eine Handbreite passt.
              */}
            {compact ? (
                <Paper variant="outlined" sx={{overflow: 'hidden'}}>
                    <MobileAgenda
                        events={events}
                        details={details}
                        visibleDate={visibleDate}
                        onVisibleDateChange={setVisibleDate}
                        onSelect={setSelected}/>
                </Paper>
            ) : (
            <Paper variant="outlined" sx={{overflow: 'hidden'}}
                   onClickCapture={handleCalendarClickCapture}>
                <EventCalendar
                    events={events}
                    resources={resources}
                    view={calendarView}
                    onViewChange={(next) => setCalendarView(next as CalendarView)}
                    visibleDate={visibleDate.toDate()}
                    onVisibleDateChange={(next) => setVisibleDate(toDayjs(next).startOf('day'))}
                    // Schreibzugriff hängt am Server-Schalter (LabelConnect.AllowWrites) und am
                    // eigenen Kalender; danach entscheidet das `readOnly` je Termin, was Labelwin
                    // gehört und was nicht.
                    readOnly={!canWrite}
                    eventCreation={canWrite}
                    // Ziehen, Vergrößern, Anlegen und Löschen im Raster landen hier — der
                    // Kalender meldet die ganze neue Liste, den Unterschied bildet applyChanges.
                    onEventsChange={(next) => void applyChanges(next as SchedulerEvent[])}
                    defaultPreferences={{
                        // 24-Stunden-Anzeige: die Vorgabe des Bausteins ist am/pm.
                        ampm: false,
                        showWeekNumber: true,
                    }}
                    localeText={localeText}
                    dateLocale={isGerman ? deDateLocale : undefined}
                    /*
                     * `minHeight: 520` schob den Kalender auf einem iPhone SE (667 Punkte hoch)
                     * unter den Bildschirmrand — zusammen mit Kopfleiste und Seitenkopf blieb kein
                     * Platz mehr. Auf dem Telefon bemisst sich die Höhe deshalb an dem, was
                     * tatsächlich zu sehen ist (`dvh` rechnet die Adressleiste heraus).
                     */
                    sx={{
                        height: {xs: 'calc(100dvh - 230px)', md: 'calc(100vh - 260px)'},
                        minHeight: {xs: 340, md: 520},
                    }}
                />
            </Paper>
            )}

            {/*
              * Ist eine andere Person gewählt, stammt der Termin aus deren Postfach — der Weblink
              * von Graph führt dorthin und ist im eigenen Outlook nicht zu öffnen.
              */}
            <EventDetailDialog detail={selected} ownMailbox={person === null}
                               onClose={() => setSelected(null)}
                               /*
                                * Zeit buchen aus dem Termin heraus — nur mit Berechtigung und nur
                                * im eigenen Kalender: gebucht wird immer auf den angemeldeten
                                * Benutzer, aus einem fremden Kalender heraus wäre der Knopf
                                * missverständlich.
                                */
                               onBookTime={canBookTime && person === null
                                   ? (orderNumber) => { setSelected(null); setTimeOrder(orderNumber); }
                                   : undefined}
                               /* Löschen nur, was der Server auch löschen lässt — freie Termine
                                  und einzelne Abwesenheitstage im eigenen Kalender. */
                               onDelete={canWrite ? (entryId) => void deleteEntry(entryId) : undefined}/>

            <TimeEntryDialog
                open={timeOrder !== undefined}
                orderNumber={timeOrder ?? null}
                onClose={() => setTimeOrder(undefined)}/>

            <EntryEditorDialog
                open={entryDraft !== undefined}
                draft={entryDraft ?? null}
                onClose={() => setEntryDraft(undefined)}
                onCreated={load}/>

            {/*
              * Rohdaten der Antwort — Werkzeug für das Abnehmen der Anbindung gegen die Plantafel,
              * deshalb nur für Administratoren und bewusst unauffällig am Fuß der Seite.
              */}
            {view && isAdmin && (
                <Box sx={{mt: 1.5}}>
                    <Button size="small" color="inherit" onClick={() => setShowRaw(!showRaw)}
                            startIcon={<FontAwesomeIcon icon={faCode}/>}
                            sx={{color: 'text.secondary', textTransform: 'none'}}>
                        {t('calendar.showRaw')}
                    </Button>
                    <Collapse in={showRaw}>
                        <Paper variant="outlined" sx={{mt: 1, p: 1.5, overflowX: 'auto'}}>
                            <Typography component="pre" variant="caption"
                                        sx={{m: 0, fontFamily: 'monospace', whiteSpace: 'pre'}}>
                                {JSON.stringify(view, null, 2)}
                            </Typography>
                        </Paper>
                    </Collapse>
                </Box>
            )}
        </Grid>
    );
};

export default MyCalendar;
